class PredictiveSearch extends HTMLElement {
	/**
	 * Constructor to initialize the PredictiveSearch component.
	 */
	constructor() {
		super();

		this.input = this.querySelector('input[type="search"]');
		this.predictiveSearchResults = this.querySelector('.mm-ps-results');
		this.predictiveSearchDefault = this.querySelector('.mm-ps-default');
		const isMobile = window.innerWidth < 950;

		// Ajouter les attributs ARIA initiaux
		this.setupAccessibility();

		this.input.addEventListener(
			'input',
			this.debounce((event) => {
				this.onChange(event);
			}, 300).bind(this)
		);

		// Ouvrir la recherche prédictive au focus
		this.input.addEventListener('focus', () => {
			// Focus rendu au champ apres Echap : ne pas rouvrir le panneau
			if (this.suppressFocusOpen) { this.suppressFocusOpen = false; return; }
			this.openOnFocus();
		});

		// Apres Echap le focus reste dans le champ : un clic doit pouvoir rouvrir le panneau
		this.input.addEventListener('click', () => {
			if (!this.isPanelOpen()) this.openOnFocus();
		});

		// Fermer la recherche prédictive au blur (avec délai pour permettre les clics)
		this.input.addEventListener('blur', (e) => {
			// Délai pour permettre les clics sur les résultats
			setTimeout(() => {
				// Vérifier si le focus n'est pas dans les résultats de recherche
				if (!this.contains(document.activeElement)) {
					this.closeSearch();
				}
			}, 200);
		});

		// Fermer la recherche si on clique sur le filtre
		const filter = document.querySelector('[data-filter]');
		if (filter) {
			filter.addEventListener('click', () => {
				if (this.predictiveSearchDefault?.style.display === 'flex' ||
					this.predictiveSearchResults?.style.display === 'block') {
					this.closeSearch();
					this.input.blur();
				}
			});
		}

		// Ajouter la gestion de la navigation au clavier
		this.setupKeyboardNavigation();
	}

	/**
	 * Configure les attributs d'accessibilité ARIA
	 */
	setupAccessibility() {
		// A11Y-15 : champ de recherche simple (pas de combobox sans navigation aux fleches),
		// suggestions = liens au Tab, nombre de resultats annonce dans [data-search-status]
		['role', 'aria-expanded', 'aria-controls', 'aria-owns', 'aria-haspopup', 'aria-autocomplete'].forEach(attr => this.input.removeAttribute(attr));
		this.statusRegion = this.querySelector('[data-search-status]');

		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.hidden = true;
		}
	}

	/**
	 * Configure la navigation au clavier et les événements de souris
	 */
	setupKeyboardNavigation() {
		// Créer les handlers une seule fois
		this.documentClickHandler = (event) => {
			// Fermer si on clique en dehors de la recherche
			if (this.isOpen() && !this.contains(event.target)) {
				this.close();
			}
		};

		// Focus sorti du panneau (Tab) : fermeture complete, voile compris
		this.documentFocusHandler = (event) => {
			if (this.isPanelOpen() && !this.contains(event.target)) {
				this.closeSearch();
			}
		};

		// Echap : panneau ferme en entier, focus garde (ou rendu) dans le champ
		this.documentKeydownHandler = (event) => {
			if (event.key === 'Escape' && this.isPanelOpen()) {
				this.closeSearch();
				if (document.activeElement !== this.input) {
					this.suppressFocusOpen = true;
					this.input.focus();
				}
			}
		};

		this.inputKeydownHandler = (event) => {
			if (event.key === 'Escape') {
				this.closeSearch();
			}
		};

		// Echap depuis un resultat : gere par documentKeydownHandler (fermeture complete)
		this.resultsKeydownHandler = () => {};

		// Ajouter les listeners
		document.addEventListener('click', this.documentClickHandler);
		document.addEventListener('focusin', this.documentFocusHandler);
		document.addEventListener('keydown', this.documentKeydownHandler);
		this.input.addEventListener('keydown', this.inputKeydownHandler);

		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.addEventListener('keydown', this.resultsKeydownHandler);
		}
	}

	/**
	 * Vérifie si le predictive search est ouvert
	 */
	isOpen() {
		return this.predictiveSearchResults && this.predictiveSearchResults.style.display === 'block';
	}

	/**
	 * Panneau ouvert : resultats, ou suggestions par defaut (« Produits populaires »)
	 */
	isPanelOpen() {
		return this.isOpen() || this.predictiveSearchDefault?.style.display === 'flex';
	}

	/**
	 * Close the search (both default and results)
	 */
	closeSearch() {
		const header = document.querySelector(".mm-section-header");
		const closeIconActive = this.querySelector('.mm-icon-close-input-close-active');
		const isHomePage = document.body.classList.contains('template-index') ||
			document.body.classList.contains('index');

		// Cacher les deux panneaux
		if (this.predictiveSearchDefault) {
			this.predictiveSearchDefault.style.display = 'none';
		}
		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.style.display = 'none';
		}

		// Toggle icons: hide close button
		if (closeIconActive) {
			closeIconActive.classList.remove('mm-active');
		}
		const closeButton = this.querySelector('.mm-search-close');
		if (closeButton) {
			closeButton.style.display = 'none';
		}

		// Retirer la classe mm-header-scroll seulement sur la homepage
		// Sur les autres pages (produit, collection, etc.), on garde mm-header-scroll
		if (header && isHomePage) {
			header.classList.remove('mm-header-scroll');
		}

		// Mettre à jour ARIA

		// Vider l'input
		this.input.value = '';

		// Cacher le filtre et réactiver le scroll
		this.hideFilter();
		this.enableScroll();
	}

	/**
	 * Open default predictive search on focus
	 */
	openOnFocus() {
		const searchTerm = this.input.value.trim();
		const header = document.querySelector(".mm-section-header");
		const closeIconActive = this.querySelector('.mm-icon-close-input-close-active');

		// Ajouter la classe mm-header-scroll au header
		if (header && !header.classList.contains('mm-header-scroll')) {
			header.classList.add('mm-header-scroll');
		}

		// Update position based on input position
		if (typeof getPredictiveSearchPosition === 'function') {
			getPredictiveSearchPosition();
		}

		// Si l'input est vide, afficher la vue par défaut
		if (!searchTerm.length) {
			if (this.predictiveSearchDefault) {
				// Update position FIRST
				if (typeof getPredictiveSearchPosition === 'function') {
					getPredictiveSearchPosition();
				}

				// Then show elements (to preserve positioning)
				this.predictiveSearchDefault.style.display = 'flex';
				this.predictiveSearchResults.style.display = 'none';

				// Show close button
				if (closeIconActive) {
					closeIconActive.classList.add('mm-active');
				}
				const closeButton = this.querySelector('.mm-search-close');
				if (closeButton) {
					closeButton.style.display = 'flex';
				}

				// Mettre à jour les attributs ARIA

				// Activer le filtre et empêcher le scroll
				this.showFilter();
				this.preventScroll();
			}
		} else {
			// Si du texte est déjà présent, afficher la croix et lancer la recherche
			if (closeIconActive) {
				closeIconActive.classList.add('mm-active');
			}
			const closeButton = this.querySelector('.mm-search-close');
			if (closeButton) {
				closeButton.style.display = 'flex';
			}
			this.getSearchResults(searchTerm);
		}
	}

	/**
	 * Event handler for input changes.
	 */
	onChange() {
		const searchTerm = this.input.value.trim();

		if (!searchTerm.length) {
			// Afficher la vue par défaut au lieu de fermer
			if (this.predictiveSearchDefault) {
				this.predictiveSearchDefault.style.display = 'flex';
				this.predictiveSearchResults.style.display = 'none';

				// Update position AFTER display is set
				if (typeof getPredictiveSearchPosition === 'function') {
					getPredictiveSearchPosition();
				}

			}
			return;
		}

		this.getSearchResults(searchTerm);
	}

	/**
	 * Fetch search results from the server based on the search term.
	 *
	 * @param {string} searchTerm - The term to search for.
	 */
	getSearchResults(searchTerm) {

    // fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)

		// Encoder le terme de recherche pour l'URL
		const encodedSearchTerm = encodeURIComponent(searchTerm);

		// Construire l'URL avec les paramètres pour inclure le contenu des articles
		// resources[articles][limit] permet de limiter le nombre d'articles retournés
		// Le contenu (body) des articles est recherché par défaut dans la recherche standard,
		// mais pour la recherche prédictive, cela dépend de la configuration Shopify
		const searchUrl = `/search/suggest?q=${encodedSearchTerm}&section_id=predictive-search&resources[articles][limit]=3&resources[products][limit]=3`;

		fetch(searchUrl)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}

				return response.text();
			})
			.then((text) => {
				try {
					const parsedDoc = new DOMParser().parseFromString(text, 'text/html');
					const sectionElement = parsedDoc.querySelector('#shopify-section-predictive-search');

					if (sectionElement) {
						const resultsMarkup = sectionElement.innerHTML;
						this.predictiveSearchResults.innerHTML = resultsMarkup;
						this.open();
						const statusText = this.predictiveSearchResults.querySelector('[data-search-status-text]');
						if (this.statusRegion && statusText) this.statusRegion.textContent = statusText.textContent.trim();
					} else {
						console.warn('Predictive search section not found in response');
						this.close();
					}
				} catch (parseError) {
					console.error('Error parsing predictive search response:', parseError);
					this.close();
				}
			})
			.catch((error) => {
				console.error('Predictive search error:', error);
				this.close();
			});
	}

	/**
	 * Open the predictive search results container.
	 */
	open() {
		if (!this.predictiveSearchResults || !this.predictiveSearchDefault) {
			console.warn('Predictive search elements not found');
			return;
		}

		this.predictiveSearchResults.style.display = 'block';
		this.predictiveSearchDefault.style.display = 'none';

		// Update position AFTER display is set
		if (typeof getPredictiveSearchPosition === 'function') {
			getPredictiveSearchPosition();
		}

		// Mettre à jour les attributs ARIA
		this.predictiveSearchResults.hidden = false;

		// Only add stop-scrolling if search container is not already open
		const searchContainer = document.querySelector("[data-search-container]");
		if (!searchContainer || !searchContainer.classList.contains("mm-open")) {
			document.documentElement.classList.add("stop-scrolling");
		}

		this.showFilter();

		// Empêcher le scroll sur toutes les pages
		this.preventScroll();
	}

	showFilter() {
		const filterElement = document.querySelector("[data-filter]");
		if (filterElement) {
			filterElement.classList.add("active");
		}
	}

	hideFilter() {
		const filterElement = document.querySelector("[data-filter]");
		if (filterElement) {
			filterElement.classList.remove("active");
		}
	}

	/**
	 * Empêcher le scroll sur toutes les pages
	 */
	preventScroll() {
		const isMobile = window.innerWidth < 950;

		// Sur mobile, utiliser la classe CSS existante
		if (isMobile) {
			document.documentElement.classList.add("stop-scrolling");
		} else {
			// Sur desktop, essayer Lenis d'abord, sinon utiliser CSS
			if (typeof stopLenis === 'function') {
				try {
					stopLenis();
				} catch (error) {
					console.warn('stopLenis function not available:', error);
					// Fallback: utiliser CSS
					document.body.style.overflow = 'hidden';
					document.body.style.position = 'fixed';
					document.body.style.width = '100%';
				}
			} else {
				// Fallback: utiliser CSS
				document.body.style.overflow = 'hidden';
				document.body.style.position = 'fixed';
				document.body.style.width = '100%';
			}
		}
	}

	/**
	 * Réactiver le scroll sur toutes les pages
	 */
	enableScroll() {
		const isMobile = window.innerWidth < 950;

		// Sur mobile, utiliser la classe CSS existante
		if (isMobile) {
			document.documentElement.classList.remove("stop-scrolling");
		} else {
			// Sur desktop, essayer Lenis d'abord, sinon utiliser CSS
			if (typeof startLenis === 'function') {
				try {
					startLenis();
				} catch (error) {
					console.warn('startLenis function not available:', error);
					// Fallback: utiliser CSS
					document.body.style.overflow = '';
					document.body.style.position = '';
					document.body.style.width = '';
				}
			} else {
				// Fallback: utiliser CSS
				document.body.style.overflow = '';
				document.body.style.position = '';
				document.body.style.width = '';
			}
		}
	}

	// CLEAR SEARCH *****************************************************************************
	/*********************************************************************************************
	 * Clear the search input.
	 *
	 */
	mmClearSearch() {
		if (this.input) {
			this.input.value = "";
			// Trigger input event to update predictive search
			this.input.dispatchEvent(new Event('input', { bubbles: true }));
		}
	};

	/**
	 * Close the predictive search results container.
	 */
	close() {
		if (!this.predictiveSearchResults || !this.predictiveSearchDefault) {
			console.warn('Predictive search elements not found');
			return;
		}

		// Utiliser un délai pour éviter les conflits d'événements
		setTimeout(() => {
			this.predictiveSearchResults.style.display = 'none';
			this.predictiveSearchDefault.style.display = 'flex';

			// Update position AFTER display is set
			if (typeof getPredictiveSearchPosition === 'function') {
				getPredictiveSearchPosition();
			}

			// Mettre à jour les attributs ARIA
			this.predictiveSearchResults.hidden = true;

			// Only remove stop-scrolling if search container is not open
			const searchContainer = document.querySelector("[data-search-container]");
			if (!searchContainer || !searchContainer.classList.contains("mm-open")) {
				document.documentElement.classList.remove("stop-scrolling");
			}

			this.hideFilter();

			// Effacer automatiquement le contenu de l'input lors de la fermeture
			this.mmClearSearch();

			// Réactiver le scroll sur toutes les pages
			this.enableScroll();
		}, 10);
	}

	/**
	 * Debounce function to limit the rate at which a function can fire.
	 *
	 * @param {Function} fn - The function to debounce.
	 * @param {number} wait - The number of milliseconds to delay.
	 * @returns {Function} - The debounced function.
	 */
	debounce(fn, wait) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}

	/**
	 * Nettoyer les event listeners quand l'élément est détruit
	 */
	disconnectedCallback() {
		if (this.documentClickHandler) {
			document.removeEventListener('click', this.documentClickHandler);
		}
		if (this.documentFocusHandler) {
			document.removeEventListener('focusin', this.documentFocusHandler);
		}
		if (this.documentKeydownHandler) {
			document.removeEventListener('keydown', this.documentKeydownHandler);
		}
		if (this.input && this.inputKeydownHandler) {
			this.input.removeEventListener('keydown', this.inputKeydownHandler);
		}
		if (this.predictiveSearchResults && this.resultsKeydownHandler) {
			this.predictiveSearchResults.removeEventListener('keydown', this.resultsKeydownHandler);
		}
	}
}

// Define the custom element
customElements.define('predictive-search', PredictiveSearch);

// Fonction globale pour effacer la recherche
window.mmClearSearch = function() {
	const predictiveSearchElements = document.querySelectorAll('predictive-search');
	predictiveSearchElements.forEach(predictiveSearch => {
		if (predictiveSearch && typeof predictiveSearch.mmClearSearch === 'function') {
			predictiveSearch.mmClearSearch();
		}
	});
};

// Fonction globale pour fermer complètement la recherche prédictive
window.mmCloseSearch = function() {
	const predictiveSearchElements = document.querySelectorAll('predictive-search');
	predictiveSearchElements.forEach(predictiveSearch => {
		if (predictiveSearch && typeof predictiveSearch.closeSearch === 'function') {
			predictiveSearch.closeSearch();
			// Blur l'input pour fermer complètement
			const input = predictiveSearch.querySelector('input[type="search"]');
			if (input) {
				input.blur();
			}
		}
	});
};

// Utiliser event delegation pour gérer les clics sur les boutons de fermeture
// Cela fonctionne quelle que soit la structure DOM ou le moment d'initialisation
document.addEventListener('click', function(e) {
	// Trouver le bouton .mm-icon-search le plus proche
	const button = e.target.closest('predictive-search .mm-icon-search');
	if (!button) return;

	const closeIcon = button.querySelector('.mm-icon-close-input');
	// Seulement fermer si l'icône de fermeture est visible (recherche ouverte)
	if (closeIcon && window.getComputedStyle(closeIcon).display !== 'none') {
		e.stopPropagation();
		e.preventDefault();
		window.mmCloseSearch();
	}
});

// S'assurer que les SVGs ne bloquent pas les clics. Le script est charge a la
// premiere intention de recherche (cf. scripts.liquid), donc souvent apres
// DOMContentLoaded : on execute tout de suite si le DOM est deja pret.
const mmUnblockSearchIcons = function() {
	const searchIcons = document.querySelectorAll('.mm-icon-search-input, .mm-icon-close-input');
	searchIcons.forEach(function(svg) {
		svg.style.pointerEvents = 'none';
	});
};
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', mmUnblockSearchIcons);
} else {
	mmUnblockSearchIcons();
}

// Fonction globale pour positionner la recherche
window.getPredictiveSearchPosition = function() {
	const searchInput = document.querySelector("input.search-header");
	const predictiveSearchElements = document.querySelectorAll(".mm-ps-results");
	const predictiveSearchDefaultElements = document.querySelectorAll(".mm-ps-default");
	const header = document.querySelector(".mm-section-header");
	// Logique mobile dès qu'on est tactile (peu importe la largeur) ou sous le breakpoint burger.
	// Synchro CSS : (hover: none) and (pointer: coarse) + max-width: 1400px dans mm-search.css.
	const isTouchDevice = typeof mmIsTouchDevice === 'function'
		? mmIsTouchDevice()
		: window.matchMedia("(hover: none) and (pointer: coarse)").matches;
	const isMobile = window.innerWidth <= 1400 || isTouchDevice;

	if (typeof isDevelopment !== 'undefined' && isDevelopment) {
		console.log('🔍 Elements found:', {
			searchInput: !!searchInput,
			predictiveSearchCount: predictiveSearchElements.length,
			predictiveSearchDefaultCount: predictiveSearchDefaultElements.length,
			header: !!header,
			isMobile
		});
	}

	if (!searchInput) {
		console.warn('❌ Search input not found');
		return;
	}

	let topPosition;

	if (isMobile) {
		// Mobile: position exactement sous le header
		if (header) {
			const headerRect = header.getBoundingClientRect();
			topPosition = headerRect.bottom;
			if (typeof isDevelopment !== 'undefined' && isDevelopment) {
				console.log('📱 Mobile mode - Header found:', {
					bottom: headerRect.bottom,
					height: headerRect.height,
					top: headerRect.top,
					calculatedTop: topPosition
				});
			}
		} else {
			console.warn('❌ Header not found in mobile mode, using input position');
			const inputRect = searchInput.getBoundingClientRect();
			topPosition = inputRect.bottom;
		}
	} else {
		// Desktop: position 10px sous l'input
		const inputRect = searchInput.getBoundingClientRect();
		topPosition = inputRect.bottom + 10;

		// Fallback si l'input n'est pas encore positionné correctement
		if (topPosition < 60) {
			topPosition = 90;
		}
	}

	// Positionner TOUS les éléments predictive-search
	predictiveSearchElements.forEach(el => {
		el.style.top = `${topPosition}px`;
	});

	// Positionner TOUS les éléments predictive-search-default
	predictiveSearchDefaultElements.forEach(el => {
		el.style.top = `${topPosition}px`;
	});

	if (typeof isDevelopment !== 'undefined' && isDevelopment) {
		console.log('✅ Search positioned at:', topPosition, 'px');
	}
};

// Appeler au chargement de la page (wrapped in rAF to avoid forced layout reflow)
function schedulePredictiveSearchPosition() {
	requestAnimationFrame(window.getPredictiveSearchPosition);
}
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', schedulePredictiveSearchPosition);
} else {
	schedulePredictiveSearchPosition();
}

// Appeler lors du resize (debounced)
let resizeTimeout;
window.addEventListener('resize', () => {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(schedulePredictiveSearchPosition, 150);
});